import React from 'react';
import { PackCard, PackCardSkeleton } from 'components/Cards/PackCard';
import { CommonCard } from 'types';
import { isNotUndefined } from 'utils/predicates';
import { packCardsPerPage } from '../../constants';

// Without this wrapper the Carousel tries to inject invalid props that create
// React warnings
const ObserverWrapper = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    return <>{children}</>;
};

export const PackCardConfig = {
    cardComponent: PackCard as any,
    fallbackComponent: PackCardSkeleton,
    slidesPerPage: packCardsPerPage,
    imageHeight: undefined,
};

export function getCardItems<I>(
    items: Array<I>,
    cardComponent: CommonCard<I>,
    isFetching: boolean,
    noMoreItems: boolean,
    skeletonData: React.ReactElement[],
    fallbackComponent: React.ComponentType,
    observerComponent?: React.ComponentType,
    showMoreCard?: React.ReactElement,
): Array<React.ReactElement> {
    const ObserverComponent = observerComponent;
    const FallbackComponent = fallbackComponent;

    let cards: Array<React.ReactElement> = (items as Array<null | I>)
        .map((item, index) => {
            if (!item) {
                return undefined;
            }

            const Card = cardComponent as CommonCard<I>;

            return (
                <Card
                    key={
                        // Append the index to the ID so if there are duplicate
                        // items we don't get errors
                        (item as { guid?: string })?.guid + String(index) ||
                        String(index)
                    }
                    id={(item as { guid?: string })?.guid || String(index)}
                    item={item}
                />
            );
        })
        .filter(isNotUndefined);

    // Queue up placeholders while observing is active, or if we're fetching
    if (
        !noMoreItems &&
        (observerComponent || (!observerComponent && isFetching))
    ) {
        cards = [...cards, ...skeletonData];
    }

    // replace one of the skeletons with an observer
    if (!noMoreItems && ObserverComponent) {
        cards.splice(
            -skeletonData.length,
            1,
            <ObserverWrapper key="observer">
                <ObserverComponent />
                <FallbackComponent />
            </ObserverWrapper>,
        );
    }

    if (showMoreCard) {
        cards = [...cards, showMoreCard];
    }

    return cards;
}
